import React, { useState, useEffect, useMemo } from 'react';
import { Icon } from './icons';
import { Modal } from './sheets';
import { fmt, money } from './format';

const KIND_LABEL = {
  removeRecurring: 'Cancel',
  scaleRecurring: 'Scale',
  addRecurring: 'Add',
};

function describeDelta(d, seriesByKey) {
  if (!d || !d.kind) return 'Invalid delta';
  if (d.kind === 'removeRecurring') {
    const s = seriesByKey[d.seriesKey];
    if (!s) return `Cancel (deleted series)`;
    return `Cancel ${s.desc} · ${fmt(s.amt, { cents: false })}/${s.cadence.toLowerCase()}`;
  }
  if (d.kind === 'scaleRecurring') {
    const s = seriesByKey[d.seriesKey];
    const pct = Math.round(((Number(d.multiplier) || 1) - 1) * 100);
    const sign = pct >= 0 ? '+' : '';
    if (!s) return `Scale (deleted series) by ${sign}${pct}%`;
    return `${sign}${pct}% on ${s.desc}${d.startDate ? ` from ${d.startDate}` : ''}`;
  }
  if (d.kind === 'addRecurring') {
    const sign = d.type === 'income' ? '+' : '-';
    return `Add ${d.desc}: ${sign}${fmt(d.amount, { cents: false })}/${d.recurrenceType || 'monthly'}`;
  }
  return d.kind;
}

function DeltaBuilder({ recurring, onAdd, onCancel }) {
  const [mode, setMode] = useState('cancel'); // cancel | scale | add
  const [seriesKey, setSeriesKey] = useState(recurring[0]?.key || '');
  const [multiplier, setMultiplier] = useState('1.0');
  const [addDesc, setAddDesc] = useState('');
  const [addAmount, setAddAmount] = useState('');
  const [addType, setAddType] = useState('expense');
  const [addRec, setAddRec] = useState('monthly');
  const [startDate, setStartDate] = useState('');

  const submit = () => {
    if (mode === 'cancel' && seriesKey) onAdd({ kind: 'removeRecurring', seriesKey });
    else if (mode === 'scale' && seriesKey) {
      const m = parseFloat(multiplier);
      if (Number.isFinite(m)) onAdd({ kind: 'scaleRecurring', seriesKey, multiplier: m, startDate: startDate || undefined });
    } else if (mode === 'add' && addDesc.trim() && parseFloat(addAmount) > 0) {
      onAdd({
        kind: 'addRecurring',
        desc: addDesc.trim(),
        amount: parseFloat(addAmount),
        type: addType,
        recurrenceType: addRec,
        startDate: startDate || new Date().toISOString().slice(0, 10),
      });
    }
  };

  return (
    <div style={{ padding: 14, display: 'flex', flexDirection: 'column', gap: 12, background: 'var(--surface-2)', borderRadius: 8 }}>
      <div role="radiogroup" aria-label="Kind of change" style={{ display: 'flex', gap: 6 }}>
        {[
          ['cancel', 'Cancel one'],
          ['scale', 'Scale one'],
          ['add', 'Add new'],
        ].map(([m, lbl]) => (
          <button
            key={m}
            type="button"
            role="radio"
            aria-checked={mode === m}
            onClick={() => setMode(m)}
            className={`chip pressable ${mode === m ? 'is-active' : ''}`}
            style={{ flex: 1, justifyContent: 'center' }}
          >
            {lbl}
          </button>
        ))}
      </div>

      {(mode === 'cancel' || mode === 'scale') && (
        <div>
          <label className="field-label">Pick a recurring series</label>
          <select
            value={seriesKey}
            onChange={(e) => setSeriesKey(e.target.value)}
            className="field"
          >
            {recurring.map((r) => (
              <option key={r.key} value={r.key}>
                {r.desc} · {fmt(r.amt, { cents: false })}/{r.cadence.toLowerCase()}
              </option>
            ))}
            {recurring.length === 0 && <option value="">(no recurring items)</option>}
          </select>
        </div>
      )}

      {mode === 'scale' && (
        <div style={{ display: 'flex', gap: 8 }}>
          <div style={{ flex: 1 }}>
            <label className="field-label">Multiplier (1.10 = +10%)</label>
            <input value={multiplier} onChange={(e) => setMultiplier(e.target.value)} inputMode="decimal" className="field" />
          </div>
          <div style={{ flex: 1 }}>
            <label className="field-label">From date (optional)</label>
            <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="field" />
          </div>
        </div>
      )}

      {mode === 'add' && (
        <>
          <div>
            <label className="field-label">Description</label>
            <input value={addDesc} onChange={(e) => setAddDesc(e.target.value)} placeholder="e.g. Car payment" className="field" />
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <div style={{ flex: 1 }}>
              <label className="field-label">Amount</label>
              <input value={addAmount} onChange={(e) => setAddAmount(e.target.value)} placeholder="425" inputMode="decimal" className="field" />
            </div>
            <div style={{ flex: 1 }}>
              <label className="field-label">Type</label>
              <select value={addType} onChange={(e) => setAddType(e.target.value)} className="field">
                <option value="expense">Expense</option>
                <option value="income">Income</option>
              </select>
            </div>
            <div style={{ flex: 1 }}>
              <label className="field-label">Cadence</label>
              <select value={addRec} onChange={(e) => setAddRec(e.target.value)} className="field">
                {[['monthly','Monthly'],['semiannual','Semi-annual'],['yearly','Yearly'],['biweekly','Biweekly'],['weekly','Weekly'],['daily','Daily']].map(([c, lbl]) => (
                  <option key={c} value={c}>{lbl}</option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <label className="field-label">Start date</label>
            <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="field" />
          </div>
        </>
      )}

      <div style={{ display: 'flex', gap: 8 }}>
        <button type="button" onClick={onCancel} className="btn-quiet is-block pressable">Cancel</button>
        <button type="button" onClick={submit} className="btn-primary is-block pressable">Add change</button>
      </div>
    </div>
  );
}

function ScenarioEditor({ open, scenario, recurring, onClose, onSave, onDelete }) {
  const isEdit = !!scenario?.id;
  const [name, setName] = useState('');
  const [enabled, setEnabled] = useState(false);
  const [deltas, setDeltas] = useState([]);
  const [adding, setAdding] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  useEffect(() => {
    if (!open) return;
    setName(scenario?.name || '');
    setEnabled(!!scenario?.enabled);
    setDeltas(scenario?.deltas ? [...scenario.deltas] : []);
    setAdding(false);
    setConfirmingDelete(false);
  }, [open, scenario]);

  const seriesByKey = useMemo(() => {
    const m = {};
    recurring.forEach((r) => {
      m[r.key] = r;
    });
    return m;
  }, [recurring]);

  if (!open) return null;

  const submit = () => {
    onSave({
      id: scenario?.id,
      name: name.trim() || 'Untitled scenario',
      enabled,
      deltas,
    });
    onClose();
  };

  return (
    <Modal open={open} onClose={onClose} title={isEdit ? 'Edit scenario' : 'New scenario'} width={500}>
      <div className="sheet-body">
        <div>
          <label className="field-label" htmlFor="scenario-name">Name</label>
          <input
            id="scenario-name"
            className="field"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Cancel Netflix + new car payment"
          />
        </div>

        <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
          <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} style={{ width: 16, height: 16, accentColor: 'var(--ink)' }} />
          <span style={{ fontSize: 14, color: 'var(--ink)' }}>Show on forecast chart</span>
        </label>

        <div>
          <div className="field-label">Changes ({deltas.length})</div>
          {deltas.length === 0 ? (
            <p className="pg-note">No changes yet — add one below.</p>
          ) : (
            deltas.map((d, i) => (
              <div key={i} className="row-line" style={{ alignItems: 'center', margin: 0, width: '100%' }}>
                {/* The kind is a chip, not a coloured caps label: the accent is
                    ink now, so a tinted micro-label read as a broken link. */}
                <span className="chip" style={{ height: 22, padding: '0 8px', fontSize: 11, flexShrink: 0 }}>
                  {KIND_LABEL[d.kind] || d.kind}
                </span>
                <span style={{ flex: 1, fontSize: 13.5, color: 'var(--ink)' }}>{describeDelta(d, seriesByKey)}</span>
                <button
                  onClick={() => setDeltas(deltas.filter((_, j) => j !== i))}
                  className="pressable"
                  style={{ background: 'transparent', color: 'var(--neg)', fontSize: 12.5, flexShrink: 0 }}
                >
                  Remove
                </button>
              </div>
            ))
          )}
        </div>

        {adding ? (
          <DeltaBuilder
            recurring={recurring}
            onAdd={(d) => { setDeltas([...deltas, d]); setAdding(false); }}
            onCancel={() => setAdding(false)}
          />
        ) : (
          <button onClick={() => setAdding(true)} className="btn-quiet is-block pressable" style={{ borderStyle: 'dashed' }}>
            Add a change
          </button>
        )}

        <button onClick={submit} className="btn-primary is-block pressable">
          {isEdit ? 'Save scenario' : 'Create scenario'}
        </button>
        {/* Confirmed in place, not with window.confirm: a browser that has
            been told to stop showing this page's dialogs answers "cancel"
            without asking, and the button then silently did nothing. */}
        {isEdit && (confirmingDelete ? (
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" onClick={() => setConfirmingDelete(false)} className="btn-quiet pressable" style={{ flex: 1, justifyContent: 'center' }}>
              Keep it
            </button>
            <button
              type="button"
              onClick={() => { onDelete(scenario.id); onClose(); }}
              className="btn-danger pressable"
              style={{ flex: 1, justifyContent: 'center' }}
            >
              Delete &ldquo;{scenario.name}&rdquo;
            </button>
          </div>
        ) : (
          <button type="button" onClick={() => setConfirmingDelete(true)} className="btn-danger is-block pressable">
            Delete scenario
          </button>
        ))}
      </div>
    </Modal>
  );
}

// Scenarios live inside Cash Flow's projected-balance section rather than on a
// page of their own: toggling one redraws the chart directly above it, so the
// what-if and its effect read together instead of a tab-switch apart.
export function ScenarioPanel({ scenarios = [], recurring = [], overlays = [], baselineEnd, onSave, onDelete, onToggle }) {
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  // Colour and projection come from the chart's overlays, so a row's swatch
  // always matches the dashed line it stands for.
  const overlayById = useMemo(() => {
    const m = {};
    overlays.forEach((o) => { m[o.id] = o; });
    return m;
  }, [overlays]);

  const openEditor = (s) => { setEditing(s); setEditorOpen(true); };

  return (
    <div style={{ marginTop: 18 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginBottom: 4 }}>
        <div className="eyebrow">Scenarios</div>
        <button type="button" onClick={() => openEditor(null)} className="btn-quiet pressable" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
          <Icon name="plus" size={14} sw={2.4} /> New scenario
        </button>
      </div>

      {scenarios.length === 0 ? (
        <p className="pg-note">
          What if you cancelled a subscription, took a pay cut, or added a new recurring expense? Build it as a scenario and toggle it on to see how the forecast shifts.
        </p>
      ) : (
        scenarios.map((s) => {
          const overlay = s.enabled ? overlayById[s.id] : null;
          const end = overlay?.data?.[overlay.data.length - 1]?.balance;
          const diff = Number.isFinite(end) && Number.isFinite(baselineEnd) ? end - baselineEnd : null;
          const changes = s.deltas?.length || 0;
          return (
            <div key={s.id} className="row-line" style={{ alignItems: 'center' }}>
              <button
                type="button"
                role="switch"
                aria-checked={!!s.enabled}
                onClick={() => onToggle(s.id, !s.enabled)}
                className="pressable"
                aria-label={`Show ${s.name} on the forecast`}
                style={{
                  width: 38, height: 22, borderRadius: 99, padding: 2, flexShrink: 0,
                  background: s.enabled ? 'var(--accent)' : 'var(--surface-3)',
                  border: '1px solid var(--line)',
                  display: 'flex', alignItems: 'center',
                  justifyContent: s.enabled ? 'flex-end' : 'flex-start',
                }}
              >
                {/* --bg, not #fff: the accent is ink and inverts on dark, so a
                    hard white knob vanished into the near-white track. */}
                <span style={{ width: 16, height: 16, borderRadius: 99, background: 'var(--bg)' }} />
              </button>
              <button
                type="button"
                onClick={() => openEditor(s)}
                className="pressable"
                style={{ flex: 1, minWidth: 0, textAlign: 'left', background: 'transparent', border: 'none', padding: 0 }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14.5, fontWeight: 500, color: 'var(--ink)' }}>
                  {overlay && <span aria-hidden="true" style={{ width: 18, height: 0, borderTop: `2px dashed ${overlay.color || 'var(--ink-2)'}`, flexShrink: 0 }} />}
                  {s.name}
                </div>
                <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 2 }}>
                  {changes} change{changes === 1 ? '' : 's'}
                </div>
              </button>
              {diff !== null && (
                <span className="num" style={{ fontSize: 13, fontWeight: 600, flexShrink: 0, color: diff < 0 ? 'var(--alarm)' : 'var(--ink-2)' }}>
                  {money(diff, { sign: true, cents: false })}
                  <span style={{ fontWeight: 400, color: 'var(--ink-3)' }}> in 12 mo</span>
                </span>
              )}
            </div>
          );
        })
      )}

      <ScenarioEditor
        open={editorOpen}
        scenario={editing}
        recurring={recurring}
        onClose={() => setEditorOpen(false)}
        onSave={onSave}
        onDelete={onDelete}
      />
    </div>
  );
}
